import { NgModule, ViewChild } from '@angular/core';
import { RouterModule, Routes, ExtraOptions } from '@angular/router';

import { AuthGuard } from '../app/core/utilities/auth.guard';
import { RedeploymentAuthorize } from './core/utilities/redeployment-authorize.guard';
import { Permission } from '../app/core/literals/permission.literal';

import { NetworkPreloadingStrategyService } from './core/services/network-preloading-strategy.service';
import { HomeComponent } from './core/components/home/home.component';

import {
  faArrowRightArrowLeft, faBoxes, faBuilding, faCube, faCubes, faDesktop, faEraser, faFireExtinguisher,
  faGears, faGear, faHome, faMoneyBillTransfer, faPrint, faPuzzlePiece, faRandom, faShare, faShoppingCart, faTruck,
  faUser, faGlobeAmericas, faLocationArrow, faX, faCalendar, faTag, faSuitcase, faListUl, faForward, faServer, faBox,
  faAppleWhole, faTags, faHdd, faVideo, faUserGroup, faBriefcase, faVideoCamera, faLocationDot, faLaptop, faBarcode,
  faEdit, faSearch, faLaptopCode, faCompass, faWarning, faDollar, faThList, faInstitution, faClipboard, faUpload, faComments
} from '@fortawesome/free-solid-svg-icons';
import { RedeploymentOrderListComponent } from './modules/redeployment-orders/redeployment-order-list/redeployment-order-list.component';
import {
  SVGIcon,
  bellIcon,
  calendarIcon,
  inboxIcon,
  menuIcon,
  starOutlineIcon,
  cancelIcon,
} from "@progress/kendo-svg-icons";
import { faTimes, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { AuthUserListComponent } from './modules/auth-users/auth-user-list/auth-user-list.component';
import { ItemListComponent } from './modules/items/item-list/item-list.component';
import { BenchTemplateListComponent } from './modules/bench-templates/bench-template-list/bench-template-list.component';
import { AuthenticationService } from './core/services/authentication.serivce';
import { CollectionListComponent } from './modules/collections/collection-list/collection-list.component';
import { SecurityRoleListComponent } from './modules/security-roles/security-role-list/security-role-list.component';
import { Organizations } from './shared/literals/shared.literal';
import { Services } from './shared/literals/shared.literal';
import { MiscellaneousItemListComponent } from './modules/miscellaneous-items/miscellaneous-item-list/miscellaneous-item-list.component';
import { BundleListComponent } from './modules/bundles/bundle-list/bundle-list.component';
import { FulfillmentOrderComponent } from './modules/fulfillment-orders/fulfillment-order/fulfillment-order.component';

// Several important things happening in our Routes configuration below
// - Using loadChildren allows us to lazy load our modules - we do this so we don' load all of the modules on app start up
// - Coupled with lazy loading is something called preloading - preloading lets us load a module in the background so that it's ready when requested
// - At the bottom we define a preloadingStrategy - we're doing a custom strategy so that we can easily pick and choose which modules we want to preload as well as check network speed and make a determination off that (if user has a slow connection, we will not preload)
// - Using data, we add a preload property. We are able to access this in our custom preloading strategy to determine if we should preload the module or not. If the preload property is not supplied, we use network infomation to determine if we should preload
// - To start, we will Preload Item and Project since they are the most used.
// - Using canActivate allows us to check things during router navigation - the AuthGuard checks to see if the user is logged in, and RedeploymentAuthorize checks to see if the user has the supplied Permission

const drawerRoutes = [
  {
    path: "",
    component: HomeComponent,
    text: "Home",
    adminPage: false,
    icon: 'k-i-home',
    viewPermission: [Permission.ViewDashboard],
    viewPermissionByOrganization: [undefined],
    canActivate: [AuthGuard, (RedeploymentAuthorize(Permission.ViewDashboard, undefined))] 
  },
  {
    path: "RedeploymentOrder",
    component: RedeploymentOrderListComponent,
    text: "Orders",
    adminPage: false,
    icon: 'k-i-table-align-top-left',
    viewPermission: [Permission.ViewOrders],
    viewPermissionByOrganization: [undefined],
    canActivate: [AuthGuard, (RedeploymentAuthorize(Permission.ViewOrders, undefined))]
  },
  {
    path: "Item",
    component: ItemListComponent,
    text: "Products",
    adminPage: false,
    icon: 'k-i-photo-camera',
    viewPermission: [Permission.ViewItems],
    viewPermissionByOrganization: [undefined],
    canActivate: [AuthGuard, (RedeploymentAuthorize(Permission.ViewItems, undefined))]
  },
  {
    path: "BoxesLabel",
    component: MiscellaneousItemListComponent,
    text: "Boxes & Labels",
    adminPage: true,
    icon: 'k-i-select-box',
    viewPermission: [Permission.CreateUpdateMiscellaneousItems],
    viewPermissionByOrganization: [Services.ShippingMethodRequired],
    canActivate: [AuthGuard, (RedeploymentAuthorize(Permission.CreateUpdateMiscellaneousItems, Services.ShippingMethodRequired))] //[Organizations.ExpRealty]
  },
  {
    path: "Bundle",
    component: BundleListComponent,
    text: "Bundles",
    adminPage: true,
    icon: 'k-i-replace-all',
    viewPermission: [Permission.ViewBundles],
    viewPermissionByOrganization: [undefined],
    canActivate: [AuthGuard, (RedeploymentAuthorize(Permission.ViewBundles, undefined))]
  },
  {
    path: "BenchTemplate",
    component: BenchTemplateListComponent,
    text: "Bench Templates",
    adminPage: true,
    icon: 'k-i-files',
    viewPermission: [Permission.ViewBenchTemplates],
    viewPermissionByOrganization: [Services.GenerateBenchTemplate],
    canActivate: [AuthGuard, (RedeploymentAuthorize(Permission.ViewBenchTemplates, Services.GenerateBenchTemplate))] 
  },
  {
    path: "AuthUser",
    component: AuthUserListComponent,
    text: "Users",
    adminPage: true,
    icon: 'k-i-accessibility',
    viewPermission: [Permission.ViewUsers],
    viewPermissionByOrganization: [undefined],
    canActivate: [AuthGuard, (RedeploymentAuthorize(Permission.ViewUsers, undefined))]
    
  },
  {
    path: "Collection",
    component: CollectionListComponent,
    text: "Collections",
    adminPage: true,
    icon: 'k-i-group-collection',
    viewPermission: [Permission.ViewCollections],
    viewPermissionByOrganization: [undefined],
    canActivate: [AuthGuard, (RedeploymentAuthorize(Permission.ViewCollections, undefined))] 
  },
  {
    path: "SecurityRole",
    component: SecurityRoleListComponent,
    text: "Security Roles",
    adminPage: true,
    icon: 'k-i-lock',
    viewPermission: [Permission.ViewSecurityRoles],
    viewPermissionByOrganization: [undefined],
    canActivate: [AuthGuard, (RedeploymentAuthorize(Permission.ViewSecurityRoles, undefined))] 
  },
  {
    path: "FulfillmentOrder",
    component: FulfillmentOrderComponent,
    text: "EPC Orders",
    adminPage: true,
    icon: 'k-i-folder-open',
    viewPermission: [Permission.ViewIinvisionOrders],
    viewPermissionByOrganization: [undefined],
    canActivate: [AuthGuard, (RedeploymentAuthorize(Permission.ViewIinvisionOrders, undefined))]
  }
];

const routerOptions: ExtraOptions = {
  scrollPositionRestoration: 'enabled',
  anchorScrolling: 'enabled',
  preloadingStrategy: NetworkPreloadingStrategyService
};

@NgModule({
  imports: [RouterModule.forRoot(drawerRoutes, routerOptions)], //routes
  exports: [RouterModule],
  providers: [AuthGuard]
})
export class AppRoutingModule
{ }


